import { z } from "zod";

import { ApiFetchStatus } from "@tdp/common/constants";
import { ILogger } from "@tdp/common/telemetry";
import { parseApiResponse } from "@tdp/core/util/parseApiResponse";

import { AppSummary } from "../../appList";
import { IAppPackageFetchResult, IPackageEditorReader } from "../abstractions/IPackageEditorReader";
import { parseZipPackage } from "../utils/parseZipPackage";



const packageEditorApi = {
  manifest: (appId: string) => {
    return `/appdefinitions/${appId}/manifest`
  },
  getSchemaForValidation: () => {
    return `/v1.0/AssetMetadata/schema`;
  },
  updateUsingImport: () => { return `/appdefinitions/v2/import?overwriteIfAppAlreadyExists=true`; },
}

export interface ITdpPackageEditorService {
  logger: ILogger;
  apiFetch: typeof fetch;
}

export class TdpPackageEditorService implements IPackageEditorReader {
  private logger: ILogger;
  private apiFetch: typeof fetch;

  constructor({ apiFetch, logger }: ITdpPackageEditorService) {
    this.apiFetch = apiFetch;
    this.logger = logger;
  }
  async importApp(file: File): Promise<{ response: boolean; }> {
    const response = await this.apiFetch(packageEditorApi.updateUsingImport(), {
      method: 'POST',
      headers: {
        "Content-Type": "application/zip; charset=utf-8"
      },
      body: file,
    });
    const paresedResponse = await parseApiResponse({
      response: response,
      errorMessage: "Error",
      logger: this.logger,
      zodType: AppSummary,
    });
    return {
      response: (paresedResponse.type === ApiFetchStatus.SUCCESS)
        ? true : false
    };
  }

  async getAppPackage(appId: string): Promise<IAppPackageFetchResult> {
    const response = await this.apiFetch(packageEditorApi.manifest(appId));
    const parsedResponse = await parseApiResponse({
      response: response,
      errorMessage: "Error getting app validation list",
      logger: this.logger,
      zodType: z.string(),
    });
    const zipPackage = await parseZipPackage(parsedResponse.data || "");
    return {
      type: parsedResponse.type,
      ...(parsedResponse.errorType && { errorType: parsedResponse.errorType }),
      ...(zipPackage && { response: zipPackage }),
      ...(parsedResponse.errorMessage && { errorMessage: parsedResponse.errorMessage }),
    }
  }

  async getSchemaForValidation(schema: string): Promise<any | null> {
    const response = await this.apiFetch(packageEditorApi.getSchemaForValidation(), {
      method: 'POST',
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        url: schema
      })
    });
    try {
      const responseObject = (await response.json());
      return JSON.parse(responseObject)
    } catch (error) {
      this.logger.error("Error parsing schema response", error);
      return null;
    }
  }
}
